import _get from 'lodash/get';

import { PUT_API_MODULE_NAMES } from '@enroller/utils';
import {
  API_FIELDS_CONTACT as CONTACT,
  API_FIELDS_APPLICATION as APPLICATION,
  API_FIELDS_STUDENT_FAMILY as STUDENT_FAMILY,
} from '@enroller/admissions-components';

import { defaultValues as interviewDefaultValues, type InterviewFormValues } from './interview/InterviewFormValues';
import { createStudentFamilyDetailsResponse, FamilyFormValues, defaultValues as studentFamilyDefaultValues } from './family/FamilyFormValues';
import { defaultValues as biographyDefaultValues, BiographyFormValues } from './biography/BiographyFormValues';
import { defaultValues as dearFamilyDefaultValues, DearFamilyFormValues } from './dearFamily/DearFamilyFormValues';
import { defaultValues as photoAlbumDefaultValues, PhotoAlbumFormValues } from './photoAlbum/PhotoAlbumFormValues';
import { defaultValues as documentsDefaultValues, DocumentsFormValues } from './documents/DocumentsFormValues';
import { defaultValues as agreementsDefaultValues, AgreementsFormValues } from './agreements/AgreementsFormValues';
import { defaultValues as vaccinationsDefaultValues, VaccinationsFormValues } from './vaccinations/VaccinationsFormValues';

import { PageResponseData, StudentGQLResponse } from './queries/graphqlResponse.ts';

import {
  createStudentDetailsResponse,
  type StudentDetailsFormValues,
  defaultValues as studentDetailsDefaultValues,
} from './studentDetails/StudentDetailsFormValues';
import {
  createSchoolAndLanguagesResponse,
  type SchoolAndLanguagesFormValues,
  defaultValues as schoolAndLanguagesDefaultValues,
} from './schoolAndLanguages/SchoolAndLanguagesFormValues';
import { createHousingFormResponse, type HousingFormValues, defaultValues as housingDefaultValues } from './housing/HousingFormValues';

interface ModulesID {
  modulesID: {
    [PUT_API_MODULE_NAMES.STUDENTS_APPLICATIONS]: string;
    [PUT_API_MODULE_NAMES.CONTACTS]: string;
    [PUT_API_MODULE_NAMES.DEALS]: string;
    [PUT_API_MODULE_NAMES.ACCOUNTS]: { family: string };
  };
}

export interface StudentApplicationValues extends ModulesID {
  studentDetails: StudentDetailsFormValues;
  interview: InterviewFormValues;
  family: FamilyFormValues;
  biography: BiographyFormValues;
  schoolAndLanguages: SchoolAndLanguagesFormValues;
  housing: HousingFormValues;
  dearFamily: DearFamilyFormValues;
  photoAlbum: PhotoAlbumFormValues;
  documents: DocumentsFormValues;
  agreements: AgreementsFormValues;
  vaccinations: VaccinationsFormValues;
}

export const studentApplicationInitialValues = {
  modulesID: {
    [PUT_API_MODULE_NAMES.CONTACTS]: '',
    [PUT_API_MODULE_NAMES.DEALS]: '',
    [PUT_API_MODULE_NAMES.STUDENTS_APPLICATIONS]: '',
    [PUT_API_MODULE_NAMES.ACCOUNTS]: { family: '' },
  },
  studentDetails: studentDetailsDefaultValues,
  schoolAndLanguages: schoolAndLanguagesDefaultValues,
  interview: interviewDefaultValues,
  family: studentFamilyDefaultValues,
  biography: biographyDefaultValues,
  housing: housingDefaultValues,
  dearFamily: dearFamilyDefaultValues,
  photoAlbum: photoAlbumDefaultValues,
  documents: documentsDefaultValues,
  agreements: agreementsDefaultValues,
  vaccinations: vaccinationsDefaultValues,
};

export const mapStudentApplicationResponse = (QGLResponse: StudentGQLResponse): StudentApplicationValues => {
  const { schoolAndLanguages, studentDetails, housing, family } = getPagesDataFromResponse(QGLResponse);

  return {
    modulesID: {
      [PUT_API_MODULE_NAMES.CONTACTS]: `${studentDetails?.Student[CONTACT.ID]?.value || ''}`,
      [PUT_API_MODULE_NAMES.DEALS]: `${studentDetails?.Student[CONTACT.ID]?.value || ''}`,
      [PUT_API_MODULE_NAMES.STUDENTS_APPLICATIONS]: `${schoolAndLanguages?.[APPLICATION.ID]?.value || ''}`,
      [PUT_API_MODULE_NAMES.ACCOUNTS]: { family: family?.data?.id.value || '' },
    },
    studentDetails: createStudentDetailsResponse(studentDetails),
    schoolAndLanguages: createSchoolAndLanguagesResponse(schoolAndLanguages),
    interview: interviewDefaultValues,
    family: createStudentFamilyDetailsResponse(family),
    biography: biographyDefaultValues,
    housing: createHousingFormResponse(housing),
    dearFamily: dearFamilyDefaultValues,
    photoAlbum: photoAlbumDefaultValues,
    documents: documentsDefaultValues,
    agreements: agreementsDefaultValues,
    vaccinations: vaccinationsDefaultValues,
  };
};

const BASE_APPLICATION_PATH = 'Records.Students_Applications._data[0]';

export const getStudentFamilyId = (data: StudentGQLResponse['family']['data']) => {
  return _get(data, `${BASE_APPLICATION_PATH}.${APPLICATION.studentFamily}.${STUDENT_FAMILY.ID}.value`);
};

const getPagesDataFromResponse = (response: StudentGQLResponse): PageResponseData => {
  /*
   * Path can be extended in case if it's not a Students_Applications
   * */
  const baseAccountContactsPath = 'Records.Accounts._data[0].Contacts__r._data';

  return {
    studentDetails: _get(response, `studentDetails.${BASE_APPLICATION_PATH}`),
    housing: _get(response, `housing.${BASE_APPLICATION_PATH}`),
    schoolAndLanguages: _get(response, `schoolAndLanguages.${BASE_APPLICATION_PATH}`),
    family: {
      data: _get(response.family.data, `${BASE_APPLICATION_PATH}.${APPLICATION.studentFamily}`),
      contacts: _get(response.family.contacts, baseAccountContactsPath),
    },
  };
};
